import { createContext, use } from 'react';
import { fetchQuery, graphql, useFragment, useMutation, useRelayEnvironment } from 'react-relay';
import { useRouter } from '@tanstack/react-router';
import type { processDefinitionsQueryDeleteMutation } from './__generated__/processDefinitionsQueryDeleteMutation.graphql';
import type { processDefinitionsQueryDeployMutation } from './__generated__/processDefinitionsQueryDeployMutation.graphql';
import type { processDefinitionsQueryFragment$key } from './__generated__/processDefinitionsQueryFragment.graphql';
import type { processDefinitionsQueryStatusMutation } from './__generated__/processDefinitionsQueryStatusMutation.graphql';
import type { processDefinitionsQueryXmlQuery } from './__generated__/processDefinitionsQueryXmlQuery.graphql';

// https://github.com/jakst/tanstack-router-relay/blob/main/src/routes/index.lazy.tsx

// PANDING/LOADING STATE
// TSR - https://github.com/TanStack/router/blob/92c6c2b1ff3bbf661c6aed7fbda84c8233d0a30e/examples/react/kitchen-sink-file-based/src/routes/__root.tsx
// TSR - https://tanstack.com/router/latest/docs/framework/react/api/router/matchRouteComponent
// RR - https://www.geeksforgeeks.org/how-relay-is-different-from-redux/

export const ProcessDefinitionsQueryCtx = createContext<
    processDefinitionsQueryFragment$key | null | undefined
>(null);

export const processDefinitionsQueryFragment = graphql`
    fragment processDefinitionsQueryFragment on ProcessDefinitionPage {
        content {
            id
            definitionKey
            definitionName
            definitionVersion
            status
            startWhen
            dirtyFlag
            listName
        }

        pageInfo {
            ...listPaginationQueryFragment
        }
    }
`;

export const useProcessDefinitionsQueryFragment = () =>
    useFragment(processDefinitionsQueryFragment, use(ProcessDefinitionsQueryCtx));

export const useProcessDefinitionsQueryFragmentRef = (
    fragmentRef?: processDefinitionsQueryFragment$key
) => useFragment(processDefinitionsQueryFragment, fragmentRef);

const gqlDelete = graphql`
    mutation processDefinitionsQueryDeleteMutation($ids: [ID!]!) {
        deleteProcessDefinitions(ids: $ids)
    }
`;

export const useProcessDefinitionsDelete = () =>
    useMutation<processDefinitionsQueryDeleteMutation>(gqlDelete);

const gqlChangeStatus = graphql`
    mutation processDefinitionsQueryStatusMutation($ids: [ID!]!, $status: Status!) {
        changeProcessDefinitionStatus(ids: $ids, status: $status)
    }
`;

export const useProcessDefinitionsChangeStatus = () =>
    useMutation<processDefinitionsQueryStatusMutation>(gqlChangeStatus);

const gqlDeploy = graphql`
    mutation processDefinitionsQueryDeployMutation($id: ID!) {
        deployProcessDefinition(id: $id)
    }
`;

export const useProcessDefinitionsDeploy = () =>
    useMutation<processDefinitionsQueryDeployMutation>(gqlDeploy);

export const useProcessDefinitionsQueryRefresh = (): (() => void) => {
    const router = useRouter();
    return () => {
        router.invalidate();
    };
};

const gqlXml = graphql`
    query processDefinitionsQueryXmlQuery($id: ID!) {
        getProcessDefinition(id: $id) {
            definitionName
            definitionXml
        }
    }
`;

export const useProcessDefinitionsQueryXml = () => {
    const relayEnv = useRelayEnvironment();
    return (id: string) =>
        fetchQuery<processDefinitionsQueryXmlQuery>(relayEnv, gqlXml, { id })
            .toPromise()
            .then((result) => result?.getProcessDefinition);
};
